import { Conversation, type ConversationProps } from './Conversation'
import { type MessagingApi } from '@/features/messaging/api/messaging-api'
import { collection } from '@/infra/collection'
import {
  type ContentResource,
  type ConversationResource,
  type MessageEventResource,
  type MessageResource,
  type MessageStatusResource,
  type ParticipantResource,
  type UnreadMessageCountItemResource,
} from '@/features/messaging/api/resources/messages'
import { type CursorPaged, type CursorPageQuery } from '@/infra/paging'
import { Id } from '@/infra/id'
import type { PageActivityDescriptor } from '@/infra/page-activity'
import { runInAction, action, computed } from 'mobx'
import {
  type Observable,
  Subject,
  catchError,
  combineLatest,
  defer,
  EMPTY,
  merge,
} from 'rxjs'
import { type User } from '@/features/iam/users/User'
import { groupBy } from 'lodash-es'
import { retry } from 'fejl'
import {
  switchMap,
  share,
  concatMap,
  filter,
  mergeMap,
  map,
  concatAll,
  ignoreElements,
  tap,
} from 'rxjs/operators'
import { type Message, type MessageProps } from './Message'
import { type ParticipantProps } from './Participant'
import {
  shareBehavior,
  flowToggle,
  filterNonNil,
  batchElements,
  withAbortSignal,
} from '@/utils/rx-util'
import type {
  GetCurrentUserFn,
  PrimeMemberByPublicIdFn,
  PrimeUserFn,
  PrimeWorkspaceFn,
} from '@/features/iam'
import { type MessageStatus } from './message-status'
import type { MessageCreated, MessageEvent } from './message-events'
import { memoizeAsync } from '@/utils/memoize-decorator'
import { Team } from '@/features/iam/teams/Team'
import { type MessageType } from './message-type'
import { maybeParseDate } from '@/utils/date-util'
import { type MessageContent } from './content'
import { mapConversationMetaResourceToMeta } from './conversation-mappers'
import {
  createFallbackContent,
  mapMessageStatusResourceToDeliveryStatus,
  mapMessageTypeResourceToMessageType,
  mapParticipantTypeResourceToType,
} from './message-mappers'
import type { PrimeConversationFn } from '@/features/messaging'

/**
 * Conversation store.
 */
export class ConversationStore {
  /**
   * When marking messages as read, they go through here.
   */
  private readonly _markAsReadRequests = new Subject<Message>()

  /**
   * Used for batch-requesting online status for users. Element string represents
   * the user ID.
   */
  private readonly _fetchOnlineStatusRequests = new Subject<string>()

  /**
   * Pipeline for fetching the online status of multiple users in a batch.
   */
  private readonly _fetchOnlineStatus$ = this._fetchOnlineStatusRequests.pipe(
    // Buffer for 100ms, max 500 elements
    batchElements(100, 500),
    // Perform the actual fetch. A failed batch keeps those users' last known
    // status instead of ending status updates for everyone.
    concatMap((ids) => this._api.users.isOnline(ids).catch(() => [])),
    // Flatten the batch results so we get a stream of individual results.
    concatAll(),
    // Only keep a single subscription to the pipeline ever
    share(),
  )

  /**
   * Collection of conversations.
   */
  readonly conversations = collection({
    getDataId: (d) => d.id,
    getModelId: (m) => m.id,
    create: () => new Conversation(),
  })

  /**
   * Message events.
   */
  readonly events$: Observable<MessageEvent>

  /**
   * Stream of new messages, including messages sent by the current user.
   */
  readonly newMessages$: Observable<Message>

  /**
   * Stream of new messages, excluding messages sent by the current user.
   */
  readonly incomingMessages$: Observable<Message>

  constructor(
    private readonly _api: MessagingApi,
    private readonly _primeWorkspace: PrimeWorkspaceFn,
    private readonly _primeMember: PrimeMemberByPublicIdFn,
    private readonly _primeUser: PrimeUserFn,
    private readonly _getCurrentUser: GetCurrentUserFn,
    currentUser$: Observable<User>,
    pageActivity$: Observable<PageActivityDescriptor>,
    userActive$: Observable<boolean>,
  ) {
    this.listMessages = this.listMessages.bind(this)
    this.sendMessage = this.sendMessage.bind(this)
    this.markAsRead = this.markAsRead.bind(this)
    this.toggleMute = this.toggleMute.bind(this)
    this.muteParticipant = this.muteParticipant.bind(this)
    this.unMuteParticipant = this.unMuteParticipant.bind(this)

    // Subscribe to Conversation events.
    this.events$ = this._api.events$.pipe(
      // An event whose conversation fails to load is dropped, so later events
      // still apply.
      concatMap((e) => this._handleEvent(e).catch(() => undefined)),
      filterNonNil(),
      share(),
    )

    this.newMessages$ = this.events$.pipe(
      filter((e): e is MessageCreated => e.type === 'MessageCreated'),
      map((e) => e.message),
      share(),
    )

    this.incomingMessages$ = this.newMessages$.pipe(
      filter((m) => !m.sender.isCurrentUser),
      share(),
    )

    merge(this.events$, this.newMessages$, this._fetchOnlineStatus$).subscribe()

    // Fetch unread counts on login. A failed fetch leaves the counts that
    // realtime brings in.
    currentUser$
      .pipe(
        switchMap(() =>
          withAbortSignal((signal) => this.fetchUnreadCounts(signal)).pipe(
            catchError(() => EMPTY),
          ),
        ),
      )
      .subscribe()

    // Observe page activity.
    const pageIsActive$ = combineLatest([pageActivity$, userActive$]).pipe(
      map(
        ([pageActivity, userActive]) =>
          pageActivity.visible && pageActivity.focused && userActive,
      ),
      shareBehavior(true),
    )

    // "Mark as Read" pipeline
    currentUser$
      .pipe(
        switchMap((currentUser) =>
          this._markAsReadRequests.pipe(
            // We only care about statuses that are not already read
            // by the current user.
            filter((m) => {
              const status = m.statusFor(currentUser) ?? false
              return status && status.deliveryStatus !== 'Read'
            }),
            // While the page is active, open the flood gates.
            flowToggle(pageIsActive$),
            // Buffer requests so we can batch them.
            batchElements(100, 100),
            // Perform mark as read operation.
            mergeMap((batch) =>
              retry((onErrRetry) =>
                this._batchMarkAsRead(currentUser, batch).catch(onErrRetry),
              ),
            ),
          ),
        ),
      )
      .subscribe()
  }

  /**
   * The total unread count.
   */
  @computed
  get totalUnreadCount() {
    return this.conversations.items.reduce(
      (accum, convo) => convo.unreadCount + accum,
      0,
    )
  }

  /**
   * Ids of the jobs whose conversation has unread messages.
   */
  @computed
  get unreadJobIds(): string[] {
    return this.conversations.items.flatMap((c) =>
      c.unreadCount > 0 && c.meta.type === 'Job' ? [c.meta.id] : [],
    )
  }

  /**
   * Primes a conversation by ID without fetching it.
   */
  readonly primeConversation: PrimeConversationFn = (props) =>
    this.conversations.set({ id: props.id })

  /**
   * Streams whether a user is online. While subscribed, it refetches the
   * status whenever the user's status changes.
   */
  onlineStatusForUser(userPublicId: Id): Observable<boolean> {
    return defer(() => {
      const id = Id.assert(userPublicId)
      this._fetchOnlineStatusRequests.next(id)

      return merge(
        this._fetchOnlineStatus$.pipe(
          filter((status) => status.user_public_id === id),
          map((status) => status.online),
        ),
        // The status change event's status isn't reliable, so it only
        // triggers a fetch.
        this.events$.pipe(
          filter(
            (e) =>
              e.type === 'ParticipantStatusChanged' && e.userPublicId === id,
          ),
          tap(() => this._fetchOnlineStatusRequests.next(id)),
          ignoreElements(),
        ),
      )
    })
  }

  /**
   * Fetches a single conversation.
   * NOTE: This is memoized.
   *
   * @param conversationId
   */
  @memoizeAsync()
  async fetchConversation(conversationId: Id) {
    return this._api.conversations
      .get(Id.assert(conversationId))
      .then((resource) =>
        this.conversations.set(this._mapConversationResourceToProps(resource)),
      )
  }

  /**
   * Sends a message.
   *
   * @param conversation the conversation
   * @param params the parameters
   */
  async sendMessage(conversation: Conversation, params: SendMessageParams) {
    let result: MessageResource
    switch (params.type) {
      case 'Message':
        result = await this._api.messages.sendText(
          Id.assert(conversation.id),
          params.message,
        )
        break
      case 'Note':
        result =
          params.team === Team.none
            ? await this._api.messages.sendNote(
                Id.assert(conversation.id),
                params.message,
              )
            : await this._api.messages.sendTeamNote(
                Id.assert(conversation.id),
                Id.assert(params.team.id),
                params.message,
              )
        break
      default:
        throw new Error(`Unable to send a message of type ${params.type}`)
    }

    conversation.messages.set(
      this._mapMessageResourceToProps(result, conversation),
    )
  }

  /**
   * Sends an admin message.
   *
   * @param conversation the conversation
   * @param params the parameters
   */
  async sendAdminMessage(
    conversation: Conversation,
    params: SendMessageParams,
  ) {
    let result: MessageResource
    switch (params.type) {
      case 'Message':
        result = await this._api.messages.sendAdmin(
          Id.assert(conversation.id),
          params.message,
        )
        break
      case 'Note':
        result = await this._api.messages.sendAdminNote(
          Id.assert(conversation.id),
          params.message,
        )
        break
      default:
        throw new Error(`Unable to send a message of type ${params.type}`)
    }

    conversation.messages.set(
      this._mapMessageResourceToProps(result, conversation),
    )
  }

  /**
   * Updates a message.
   *
   * @param conversation the conversation
   * @param messageId the message id
   * @param text the text
   */
  async updateMessage(conversation: Conversation, messageId: Id, text: string) {
    const result = await this._api.messages.update(Id.assert(messageId), text)

    conversation.messages.set(
      this._mapMessageResourceToProps(result, conversation),
    )
  }

  /**
   * Fetches unread counts for a user.
   */
  async fetchUnreadCounts(signal?: AbortSignal) {
    const result = await this._api.conversations.getUnreadCounts(signal)
    runInAction(() => {
      result.forEach((item) => {
        const conversation = this.conversations.set(
          this._mapUnreadCountResourceToProps(item),
        )
        conversation.resetUnreadCount(item.unread_count)
      })
    })
  }

  /**
   * Lists a page of messages for a conversation.
   */
  async listMessages(
    { conversationId, teamId, paging }: ListMessagesQuery,
    signal?: AbortSignal,
  ): Promise<CursorPaged<Message>> {
    const conversation = await this.fetchConversation(conversationId)
    const response = await this._api.messages.list(
      Id.assert(conversationId),
      {
        limit: paging.limit,
        after: paging.after,
        conversation_id: Id.assert(conversationId),
        team_id: teamId || undefined,
        include_status: true,
      },
      signal,
    )

    return {
      data: conversation.messages.set(
        response.data.map((resource) =>
          this._mapMessageResourceToProps(resource, conversation),
        ),
      ),
      next: response.cursor,
      total: response.total,
    }
  }

  /**
   * Fetches the total amount of human messages in a conversation.
   */
  async fetchHumanMessagesTotalCount(
    conversationId: Id,
    workspaceId: Id,
  ): Promise<void> {
    if (!Id.some(conversationId) || !Id.some(workspaceId)) {
      return
    }
    const conversation = this.conversations.find((c) => c.id === conversationId)
    if (conversation === undefined) {
      return
    }

    const humanMessages = await this._api.conversations.getHumanMessages(
      conversationId,
      workspaceId,
    )

    conversation.setHumanMessagesCount(humanMessages.length)
  }

  /**
   * Marks the message as read.
   *
   * @param message
   */
  markAsRead(message: Message) {
    this._markAsReadRequests.next(message)
  }

  /**
   * Toggles whether the current user muted the conversation.
   */
  async toggleMute(conversation: Conversation) {
    const participant = conversation.participants.get(
      Id.assert(this._getCurrentUser().publicId),
    )
    if (participant) {
      await (participant.muted
        ? this.unMuteParticipant(conversation)
        : this.muteParticipant(conversation))
      return participant
    }
    return null
  }

  /**
   * Mutes the conversation for the current user.
   */
  async muteParticipant(conversation: Conversation) {
    return this._updateMuted(conversation, true)
  }

  /**
   * Unmutes the conversation for the current user.
   */
  async unMuteParticipant(conversation: Conversation) {
    return this._updateMuted(conversation, false)
  }

  private async _updateMuted(conversation: Conversation, muted: boolean) {
    if (conversation === Conversation.none) {
      return
    }
    const id = Id.assert(conversation.id)
    const participant = await this._api.participants.update({
      muted,
      user_id: Id.assert(this._getCurrentUser().publicId),
      conversation_id: id,
    })
    return conversation.participants.set(
      this._mapParticipantResourceToProps(
        participant,
        conversation.workspace.id,
      ),
    )
  }

  /**
   * Mass marks as read. Called from the observable setup above.
   *
   * @param currentUser
   * @param batch
   */
  private async _batchMarkAsRead(currentUser: User, batch: Message[]) {
    const grouped = groupBy(batch, (x) => x.conversation.workspace.id)
    return Promise.all(
      Object.keys(grouped).map(async (workspaceId) => {
        const msgs = grouped[workspaceId]
        runInAction(() => msgs.forEach((m) => m.markRead(currentUser)))
        await this._api.messages
          .markRead(
            workspaceId,
            msgs.map((m) => Id.assert(m.id)),
          )
          .catch((err) => {
            console.error('Failed to mark messages as read', err)
          })
      }),
    )
  }

  /**
   * Handles a real-time event and transforms it to a nicer shape.
   *
   * @param event
   */
  @action.bound
  private async _handleEvent(
    event: MessageEventResource,
  ): Promise<MessageEvent | undefined> {
    if (!event.type) {
      return
    }
    switch (event.type) {
      case 'MESSAGE_CREATED': {
        const convo = await this.fetchConversation(
          event.payload.conversation_id,
        )
        const message = convo.messages.set(
          this._mapMessageResourceToProps(event.payload, convo),
        )
        return { message, type: 'MessageCreated' }
      }
      case 'MESSAGE_UPDATED': {
        const convo = await this.fetchConversation(
          event.payload.conversation_id,
        )
        const message = convo.messages.set(
          this._mapMessageResourceToProps(event.payload, convo),
        )
        return { message, type: 'MessageUpdated' }
      }
      case 'MESSAGE_DELETED': {
        const convo = await this.fetchConversation(
          event.payload.conversation_id,
        )
        convo.messages.remove(event.payload.id)
        break
      }
      case 'MESSAGE_DELIVERED': {
        const convo = await this.fetchConversation(
          event.payload.conversation_id,
        )
        const message = convo.messages.get(event.payload.id)
        if (message) {
          message.markDelivered(
            this._primeUser({ publicId: event.payload.user_public_id }),
          )
        }
        break
      }
      case 'MESSAGE_READ': {
        const convo = await this.fetchConversation(
          event.payload.conversation_id,
        )
        const message = convo.messages.get(event.payload.id)
        if (message) {
          message.markRead(
            this._primeUser({ publicId: event.payload.user_public_id }),
          )
        }
        break
      }
      case 'PARTICIPANT_ADDED': {
        const userPublicId = event.payload.user_public_id
        if (!userPublicId) {
          break
        }
        const convo = await this.fetchConversation(
          event.payload.conversation_id,
        )
        convo.participants.set([
          this._mapParticipantResourceToProps(
            { user_id: event.payload.user_id, user_public_id: userPublicId },
            convo.workspace.id,
          ),
        ])
        break
      }
      case 'PARTICIPANT_REMOVED': {
        const userPublicId = event.payload.user_public_id
        if (!userPublicId) {
          break
        }
        const convo = await this.fetchConversation(
          event.payload.conversation_id,
        )
        convo.participants.remove(userPublicId)
        break
      }
      case 'PARTICIPANT_STATUS_CHANGED': {
        return {
          userPublicId: Id(event.payload.user_public_id),
          type: 'ParticipantStatusChanged',
        }
      }
    }
  }

  private _mapConversationResourceToProps(
    resource: ConversationResource,
  ): Partial<ConversationProps> {
    return {
      id: resource.id,
      workspace: this._primeWorkspace({ id: resource.workspace_id }),
      active: resource.active,
      name: resource.name,
      meta: resource.meta
        ? mapConversationMetaResourceToMeta(resource.meta, resource.id)
        : undefined,
      dateCreated: maybeParseDate(resource.date_created),
      dateModified: maybeParseDate(resource.date_modified),
      participants: resource.participants.map((p) =>
        this._mapParticipantResourceToProps(p, resource.workspace_id),
      ),
    }
  }

  private _mapUnreadCountResourceToProps(
    resource: UnreadMessageCountItemResource,
  ): Partial<ConversationProps> {
    return {
      id: resource.conversation_id,
      workspace: this._primeWorkspace({ id: resource.workspace_id }),
      meta: resource.meta
        ? mapConversationMetaResourceToMeta(
            resource.meta,
            resource.conversation_id,
          )
        : undefined,
      // We use the dateModified on the Conversation
      // as a way of determining job activity.
      dateModified: maybeParseDate(resource.last_message.date_modified),
    }
  }

  /**
   * Also maps the partial participant of a `PARTICIPANT_ADDED` event.
   */
  private _mapParticipantResourceToProps(
    resource: Partial<ParticipantResource>,
    workspaceId: Id,
  ): Partial<ParticipantProps> {
    return {
      userPublicId: resource.user_public_id,
      member: this._primeMember({
        workspaceId,
        userId: resource.user_id,
        userPublicId: resource.user_public_id,
      }),
      type: resource.type
        ? mapParticipantTypeResourceToType(resource.type)
        : undefined,
      active: resource.active,
      lastSeen: resource.last_seen,
      online: resource.online,
      muted: resource.muted,
    }
  }

  private _mapMessageResourceToProps(
    resource: MessageResource,
    conversation: Conversation,
  ): Partial<MessageProps> {
    return {
      id: resource.id,
      sender: this._primeMember({
        workspaceId: conversation.workspace.id,
        userId: resource.user_id,
        userPublicId: resource.user_public_id,
      }),
      messageType: mapMessageTypeResourceToMessageType(resource.content.type),
      dateCreated: maybeParseDate(resource.date_created),
      dateModified: maybeParseDate(resource.date_modified),
      recipientStatuses: resource.statuses?.map((status) =>
        this._mapMessageStatusResourceToStatus(status, conversation),
      ),
      // The API way of describing content is not ideal.
      // In the Work Portal, we want Content to mean the actual type of content,
      // and the other flags such as Note and System Message to be attributes.
      // We also currently don't use the File content.
      content: this._mapContentResourceToContent(
        resource.content,
        conversation,
      ),
    }
  }

  private _mapContentResourceToContent(
    resource: ContentResource,
    conversation: Conversation,
  ): MessageContent {
    switch (resource.type) {
      case 'CALL':
        return {
          type: 'Call',
        }
      case 'EXTERNAL_REFERENCE':
        // External references have a single external ID and a type.
        switch (resource.external_type) {
          case 'worklayer.jobs.Progression':
            return {
              type: 'Progression',
              stepId: resource.external_id,
              jobId:
                conversation.meta.type === 'Job'
                  ? conversation.meta.id
                  : undefined,
            }
          case 'worklayer.jobs.assistant.OpenAI':
            return {
              type: 'Assistant',
              text: resource.text,
              externalReplyId: resource.external_id,
            }
        }
        return createFallbackContent(resource.text)
      case 'TEXT':
      case 'FILE':
      case 'SYSTEM':
      case 'NOTE':
      case 'TEAM_NOTE':
      case 'ADMIN_NOTE':
      case 'ADMIN':
        return createFallbackContent(resource.text)
    }
    // The API may send values this build doesn't know yet.
    return createFallbackContent((resource as ContentResource).text)
  }

  private _mapMessageStatusResourceToStatus(
    resource: MessageStatusResource,
    conversation: Conversation,
  ): MessageStatus {
    return {
      id: resource.id,
      deliveryStatus: mapMessageStatusResourceToDeliveryStatus(resource.status),
      member: this._primeMember({
        workspaceId: conversation.workspace.id,
        userId: resource.user_id,
        userPublicId: resource.user_public_id,
      }),
    }
  }
}

/**
 * Query for listing a conversation's messages.
 */
export interface ListMessagesQuery {
  conversationId: Id
  teamId?: Id | null
  paging: CursorPageQuery
}

/**
 * What to send to a conversation.
 */
export interface SendMessageParams {
  message: string
  team: Team
  type: MessageType
}
